import { formatDate } from "__utilities/functions/public";
import { overlayContext } from "_data/context/overlay";
import usePayment from "_data/hooks/payment/usePayment";
import { useContext, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import AccountViewButtons from "./AccountViewButtons";
import ConfirmationPopup from "__utilities/components/ConfirmationPopup";
import { deleteUserRequest } from "_data/services/auth/service";
import { useMsal } from "@azure/msal-react";
import { clearUserData } from "msal/MsalAuthProvider";
import BillingForm from "_features/BillingForm";
import BillingHistory from "_features/BillingHistory";
import { downloadPDFFile } from "__utilities/functions/public";
import { AppConfig } from "AppConfig";
import {
  isPaymentPending,
  isSubscriptionEnding,
  isSubscriptionRenewing,
  subscriptionPeriodEnd,
} from "__utilities/functions/subscription";
import {
  reportPaymentError,
  requiresBillingDetails,
} from "_data/services/payment/paymentErrors";
import { useNavigate } from "react-router-dom";

export default function AccountView() {
  const { setOverlay } = useContext(overlayContext);
  const { userInfo, setUserInfo } = useOutletContext();
  const [billingHistory, setBillingHistory] = useState([]);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [showBillingDetails, setShowBillingDetails] = useState(false);
  const subscriberSince = `${formatDate(userInfo?.subscriberSince)}`;
  const memberSince = `${formatDate(userInfo?.memberSince)}`;
  const periodEndDate = formatDate(subscriptionPeriodEnd(userInfo));

  /**
   * The four states worth telling the customer apart. Note that "ophører" (ends) and
   * "fornys" (renews) are both reached with the subscription status still Active -- the flag
   * is what differs.
   */
  const subscriptionSummaryText = isPaymentPending(userInfo)
    ? "Betalingen behandles"
    : userInfo?.licenseHasExpired
      ? "Dit abonnement er udløbet"
      : isSubscriptionEnding(userInfo)
        ? "Dit abonnement ophører den"
        : isSubscriptionRenewing(userInfo)
          ? "Dit abonnement fornys automatisk den"
          : "Dit abonnement er gyldigt til";
  const {
    getUserInfoData,
    getBillingHistory,
    getInitialBillingAddress,
    sendBillingAddress,
    unsubscribePayment,
    switchPayment,
    getSubscriptionTypes,
    initiatePayment,
  } = usePayment();
  const { instance } = useMsal();
  const navigate = useNavigate();

  const signOut = () => {
    instance.logout().catch((error) => {
      console.log(error);
    });
  };

  const getSubscriptionTypesData = async () => {
    await getSubscriptionTypes((success, response) => {
      if (success) {
        setSubscriptionData(response);
      }
    });
  };

  /**
   * Changes the plan at the next renewal. Refused if the subscription is already set to end --
   * the buttons only offer resume in that state, so reaching it means this view was working from
   * stale state, and the refresh below re-renders the right action rather than scolding the user.
   */
  const switchSubscription = () => {
    if (userInfo?.subscriptionType) {
      const newType = userInfo?.subscriptionType === "Year" ? "Month" : "Year";
      switchPayment(newType, (success, errorCode) => {
        if (!success) {
          reportPaymentError(errorCode);
        }
        refreshUserInfo();
      });
    }
    setOverlay({ show: false, content: <></> });
  };

  /**
   * Resumes a subscription that was set to end. There is no separate resume endpoint -- the
   * subscribe call resumes, changes plan or starts fresh depending on the user's state -- and
   * the flag flips back with no new payment.
   */
  const resumeSubscription = () => {
    initiatePayment(
      userInfo?.subscriptionType ?? "Month",
      (success, errorCode) => {
        if (!success) {
          if (requiresBillingDetails(errorCode)) {
            navigate("/billing", {
              state: {
                subscriptionType: userInfo?.subscriptionType ?? "Month",
              },
            });
            return;
          }
          reportPaymentError(errorCode);
        }
        refreshUserInfo();
      },
    );
  };

  const getBillingHistoryData = async () => {
    await getBillingHistory((success, response) => {
      if (success) {
        setBillingHistory(response);
      }
    });
  };

  const refreshUserInfo = async () => {
    await getUserInfoData((success, userInfoResponse) => {
      if (success) {
        setUserInfo(userInfoResponse);
      }
    });
  };

  const downloadInvoicePdf = async (token) => {
    downloadPDFFile(
      `${AppConfig.SERVICE_BASE_URL}/api/v1/payment/billinghistory/download/${token}`,
    );
  };

  const deleteUser = async () => {
    await deleteUserRequest(() => {
      clearUserData();
      signOut();
    });
  };

  useEffect(() => {
    getSubscriptionTypesData();
    getBillingHistoryData();
  }, []);

  /**
   * Invoice download tokens expire after 24 hours, and the link opens as a browser navigation
   * whose failure we cannot dress up -- the customer would see the browser's own error page.
   * Re-reading billing history on focus mints fresh tokens for a page left open overnight.
   *
   * Deliberately not done inside the click: an async hop before opening a tab is exactly what
   * popup blockers stop, which would trade a rare failure for a common one.
   */
  useEffect(() => {
    const refreshTokens = () => getBillingHistoryData();

    window.addEventListener("focus", refreshTokens);
    return () => window.removeEventListener("focus", refreshTokens);
  }, []);

  return (
    <div className="flex flex-col w-full h-full justify-start items-start gap-y-6 p-5 tall:p-11">
      <div className="flex flex-col gap-2">
        <p className="text-primaryBlue  text-xl tall:text-[24px] font-latoBold">
          Min konto
        </p>

        <p
          onClick={() =>
            setOverlay({
              show: true,
              content: (
                <ConfirmationPopup
                  text={
                    "Din konto vil blive slettet permanent. Vil du fortsætte?"
                  }
                  cancelPopup={() =>
                    setOverlay({ show: false, content: <></> })
                  }
                  confirmContinue={() => deleteUser()}
                />
              ),
            })
          }
          className="text-primaryBlue text-sm tall:text-base font-latoBold cursor-pointer underline"
        >
          Slet konto
        </p>
      </div>

      <div className="flex flex-row w-full justify-start gap-x-10 tall:gap-x-20">
        <div className="flex flex-col w-1/5">
          <p className="text-primaryBlue text-standardXLarge tall:text-2xl font-latoBold">
            Abonnement:
          </p>
          {userInfo?.memberSince && (
            <p className="text-textLightGray text-xs tall:text-base font-latoBold">
              {`Medlem siden ${memberSince}`}
            </p>
          )}
          <p className="text-textLightGray text-xs tall:text-base font-latoBold">
            {userInfo?.subscriberSince
              ? `Abonnent siden ${subscriberSince}`
              : "Ikke abonneret"}
          </p>
        </div>
        {userInfo?.subscriptionType && (
          <div className="flex flex-col w-full h-[300px] bg-[#F0F3F5] border border-[#EAECF0]">
            <div className="flex flex-row w-full h-3/5 items-center">
              <div className="flex flex-col w-full gap-y-2 p-5">
                <p className="text-[24px] text-primaryBlue font-latoBold">
                  {userInfo?.executionLicense}
                </p>
                {/* Derived from the cancel-at-period-end flag, not the status: a cancelled
                    subscription stays Active until the paid period runs out. */}
                <div className="flex flex-row gap-x-2">
                  <p className="text-standardLarge text-textGray font-latoLight">
                    {subscriptionSummaryText}
                  </p>
                  {periodEndDate && (
                    <p className="text-standardLarge text-primaryBlue font-latoBold">
                      {periodEndDate}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-row h-full px-5 py-11 font-latoBold text-primaryBlue">
                <div className="flex flex-col h-full justify-start items-start text-lg tall:text-[24px] translate-y-5 tall:translate-y-4">
                  {
                    subscriptionData?.find(
                      (el) => el.type === userInfo?.subscriptionType,
                    )?.currency
                  }
                </div>
                {/* The headline is the VAT-exclusive base price (B2B convention). The split
                    comes from the API; never compute it here. */}
                <div className="flex flex-col h-full justify-center items-center text-5xl tall:text-[60px]">
                  {subscriptionData
                    ?.find((el) => el.type === userInfo?.subscriptionType)
                    ?.baseAmount.toLocaleString("da-DK", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                </div>
                <div className="flex flex-col h-full justify-end items-end text-sm tall:text-standardLarge -translate-y-6 tall:-translate-y-4">
                  {userInfo?.subscriptionType === "Year" ? (
                    <span>per&nbsp;år</span>
                  ) : (
                    <span>om&nbsp;måneden</span>
                  )}
                </div>
              </div>
            </div>

            <AccountViewButtons
              userInfo={userInfo}
              refreshUserInfo={refreshUserInfo}
              setOverlay={setOverlay}
              unsubscribePayment={unsubscribePayment}
              switchSubscription={switchSubscription}
              resumeSubscription={resumeSubscription}
            />
          </div>
        )}
      </div>

      <div className="flex flex-row w-full justify-start gap-x-10 tall:gap-x-20 mt-12 pb-10">
        <div className="flex flex-col w-1/5 gap-y-4">
          <p className="text-primaryBlue text-standardXLarge tall:text-2xl font-latoBold">
            Fakturering:
          </p>
          <div className="flex flex-col">
            <p
              onClick={() => setShowBillingDetails(false)}
              className={`${
                !showBillingDetails ? "text-primaryBlue" : "text-textXLightGray"
              } text-standardSmall tall:text-lg font-latoBold cursor-pointer`}
            >
              Faktureringshistorik
            </p>
            <p
              onClick={() => setShowBillingDetails(true)}
              className={`${
                showBillingDetails ? "text-primaryBlue" : "text-textXLightGray"
              } text-standardSmall tall:text-lg font-latoBold cursor-pointer`}
            >
              Faktureringsdetaljer
            </p>
          </div>
        </div>

        <div className="flex flex-col w-full h-full">
          {!showBillingDetails ? (
            <BillingHistory
              billingHistory={billingHistory}
              downloadInvoicePdf={downloadInvoicePdf}
            />
          ) : (
            <div className="flex flex-col w-1/2 h-full">
              <BillingForm
                getInitialBillingAddress={getInitialBillingAddress}
                sendBillingAddress={sendBillingAddress}
                initialEmail={userInfo.email}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
